import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Button,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import { getExpenseDashboard, seedExpenseCategories } from '../../Redux/Actions/action';
import ErrorHandler from '../../Utils/handler';
import { toast } from 'react-toastify';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import dayjs from 'dayjs';

const ExpensesDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [dashboardData, setDashboardData] = useState({
        thisMonth: {
            totalExpenses: 0,
            totalAmount: 0,
            approvedAmount: 0,
            pendingAmount: 0,
            rejectedAmount: 0,
        },
        pendingApprovals: 0,
        categoryBreakdown: [],
        recentExpenses: [],
        monthlyTrend: [],
    });

    const fetchDashboard = async () => {
        dispatch(showLoader(true));
        try {
            const result = await dispatch(getExpenseDashboard());
            if (ErrorHandler(result)) {
                setDashboardData(result?.payload?.data || {});
            }
        } catch (error) {
            console.error('Dashboard fetch error:', error);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleSeedCategories = async () => {
        dispatch(showLoader(true));
        try {
            const result = await dispatch(seedExpenseCategories());
            if (ErrorHandler(result)) {
                toast.success(result?.payload?.message || 'Categories seeded successfully');
            }
        } catch (error) {
            toast.error('Failed to seed categories');
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, []);

    const StatCard = ({ title, value, icon, color, subtitle }) => (
        <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight={700} color={color}>
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="textSecondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            backgroundColor: `${color}15`,
                            borderRadius: 2,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    const { thisMonth, pendingApprovals, categoryBreakdown, recentExpenses } = dashboardData;

    return (
        <Box>
            <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px' }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            Expense Dashboard
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {dayjs().format('MMMM YYYY')} Overview
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" onClick={handleSeedCategories} size="small">
                            Setup Categories
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/expenses/add')}
                        >
                            Add Expense
                        </Button>
                    </Box>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Total Expenses"
                            value={`₹${thisMonth?.totalAmount?.toLocaleString() || 0}`}
                            icon={<TrendingUpIcon sx={{ color: '#3E4094', fontSize: 28 }} />}
                            color="#3E4094"
                            subtitle={`${thisMonth?.totalExpenses || 0} entries`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Approved"
                            value={`₹${thisMonth?.approvedAmount?.toLocaleString() || 0}`}
                            icon={<CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 28 }} />}
                            color="#4CAF50"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Pending Approval"
                            value={pendingApprovals || 0}
                            icon={<PendingActionsIcon sx={{ color: '#FF9800', fontSize: 28 }} />}
                            color="#FF9800"
                            subtitle={`₹${thisMonth?.pendingAmount?.toLocaleString() || 0}`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Rejected"
                            value={`₹${thisMonth?.rejectedAmount?.toLocaleString() || 0}`}
                            icon={<CancelIcon sx={{ color: '#f44336', fontSize: 28 }} />}
                            color="#f44336"
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    {/* Category Breakdown */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <CategoryIcon color="primary" />
                                <Typography variant="h6" fontWeight={600}>
                                    Category-wise Breakdown
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            {categoryBreakdown?.length > 0 ? (
                                <List dense>
                                    {categoryBreakdown.slice(0, 6).map((cat, index) => (
                                        <ListItem key={index} sx={{ px: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Typography fontSize={20}>{cat.categoryIcon || '📝'}</Typography>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={cat.categoryName || 'Unknown'}
                                                secondary={`${cat.count} expenses`}
                                            />
                                            <Typography fontWeight={600} color={cat.categoryColor || '#3E4094'}>
                                                ₹{cat.amount?.toLocaleString() || 0}
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Box textAlign="center" py={4}>
                                    <Typography color="textSecondary">No data available</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Recent Expenses */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <ReceiptIcon color="primary" />
                                    <Typography variant="h6" fontWeight={600}>
                                        Recent Expenses
                                    </Typography>
                                </Box>
                                <Button size="small" onClick={() => navigate('/expenses')}>
                                    View All
                                </Button>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            {recentExpenses?.length > 0 ? (
                                <List dense>
                                    {recentExpenses.slice(0, 6).map((expense, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{ px: 0, cursor: 'pointer' }}
                                            onClick={() => navigate(`/expenses/view/${expense._id}`)}
                                        >
                                            <ListItemText
                                                primary={expense.title}
                                                secondary={dayjs(expense.expenseDate).format('DD MMM YYYY')}
                                                primaryTypographyProps={{ noWrap: true, maxWidth: 200 }}
                                            />
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography fontWeight={600}>
                                                    ₹{expense.amount?.toLocaleString()}
                                                </Typography>
                                                <Chip
                                                    label={expense.status}
                                                    size="small"
                                                    color={
                                                        expense.status === 'Approved' ? 'success' :
                                                        expense.status === 'Pending' ? 'warning' :
                                                        expense.status === 'Rejected' ? 'error' : 'default'
                                                    }
                                                />
                                            </Box>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Box textAlign="center" py={4}>
                                    <Typography color="textSecondary">No recent expenses</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* Quick Actions */}
                <Box mt={4}>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                        Quick Actions
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item>
                            <Button variant="outlined" onClick={() => navigate('/expenses')}>
                                View All Expenses
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="outlined" onClick={() => navigate('/expenses/categories')}>
                                Manage Categories
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="outlined" onClick={() => navigate('/expenses/reports')}>
                                Generate Reports
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>
    );
};

export default ExpensesDashboard;
